import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../services/ApiService";
import BookingCalendar from "./BookingCalendar";
import CouponInput from "./CouponInput";
import logo from "../assets/logo.png";
import "./IntegratedPurchaseFlow.css";

const IntegratedPurchaseFlow = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if this is for pay-per-use only
  const payPerUseOnly = location.state?.payPerUseOnly;

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  // Data states
  const [carTypes, setCarTypes] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const steps = [
    {
      id: 1,
      title: "Select Car Type",
      description: "Choose your vehicle type",
    },
    { id: 2, title: "Choose Plan", description: "Select a pricing plan" },
    { id: 3, title: "Book Service", description: "Schedule your car wash" },
  ];
  useEffect(() => {
    fetchCarTypes();
  }, []);
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await ApiService.getPricingPlans();

        // Filter plans that are available for the selected car type
        let filteredPlans = response.data || [];
        if (selectedCarType) {
          filteredPlans = filteredPlans.filter((plan) =>
            plan.pricingPlanCarTypes?.some(
              (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
            )
          );
        }

        // If payPerUseOnly is true, only show plans with infinite validity (pay-per-use plans)
        if (payPerUseOnly) {
          filteredPlans = filteredPlans.filter((plan) => plan.validity === -1);
        }

        setPricingPlans(filteredPlans);
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
        setError("Failed to load pricing plans. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    if (selectedCarType && currentStep === 2) {
      fetchPricingPlans();
    }
  }, [selectedCarType, currentStep, payPerUseOnly]);

  const fetchCarTypes = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ApiService.getAllCarTypes();
      setCarTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching car types:", error);
      setError("Failed to load car types. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleCarTypeSelect = (carType) => {
    setSelectedCarType(carType);
    setCompletedSteps((prev) => new Set([...prev, 1]));
  };
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCompletedSteps((prev) => new Set([...prev, 2]));
  };

  // Coupon handlers
  const handleCouponApply = (couponData) => {
    setAppliedCoupon(couponData);
  };

  const handleCouponRemove = () => {
    setAppliedCoupon(null);
  };
  const handlePurchase = async () => {
    if (!selectedPlan || !selectedCarType) {
      setError("Please select both a car type and a pricing plan");
      return;
    }

    // Check if this is an infinite validity plan (pay-per-use)
    if (selectedPlan.validity === -1) {
      // For infinite validity plans, redirect to slot booking page first
      navigate("/pay-per-use-slot-booking", {
        state: {
          selectedPlan,
          selectedCarType,
          appliedCoupon,
        },
      });
      return;
    }

    try {
      setPurchasing(true);
      setError("");

      // Create purchase with optional coupon code
      const purchaseData = {
        pricingPlanId: selectedPlan.id,
        carTypeId: selectedCarType.id,
        paymentMethod: "razorpay",
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };
      const purchaseResponse = await ApiService.createPurchase(purchaseData);
      const { purchase, razorpayOrder, razorpayKeyId } = purchaseResponse.data;

      // Check if this is a zero-amount purchase (completed with coupon)
      if (purchase.paymentStatus === "completed") {
        // Purchase was completed for free with coupon
        setSuccessMessage(
          purchase.message || "Purchase completed successfully with coupon!"
        );

        // Clear any previous errors
        setError("");

        // Check plan validity to determine redirect behavior
        if (selectedPlan.validity === -1) {
          // Infinite validity - redirect to booking calendar
          setCompletedSteps((prev) => new Set([...prev, 2]));
          setCurrentStep(3);
          setPurchasing(false);
        } else {
          // Limited validity - redirect to dashboard
          setPurchasing(false);

          // Show success message briefly before redirecting
          setTimeout(() => {
            navigate("/dashboard");
          }, 3000);
        }
        return;
      }

      // Regular payment flow for non-zero amounts
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () =>
          initializePayment(purchase, razorpayOrder, razorpayKeyId);
        document.body.appendChild(script);
      } else {
        initializePayment(purchase, razorpayOrder, razorpayKeyId);
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setError(error.message || "Purchase failed. Please try again.");
      setPurchasing(false);
    }
  };

  const initializePayment = (purchase, razorpayOrder, razorpayKeyId) => {
    const options = {
      key: razorpayKeyId,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency,
      name: "Steam & Drive",
      description: `${selectedPlan.name} for ${selectedCarType.name}`,
      order_id: razorpayOrder.id,
      handler: async (response) => {
        try {
          // Complete the purchase with payment details
          const completionData = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          };
          await ApiService.completePurchase(purchase.id, completionData);

          // Clear any previous errors
          setError("");

          // Check plan validity to determine redirect behavior
          if (selectedPlan.validity === -1) {
            // Infinite validity - redirect to booking calendar
            setSuccessMessage(
              "Payment successful! You can now book your service."
            );
            setCompletedSteps((prev) => new Set([...prev, 2]));
            setCurrentStep(3);
            setPurchasing(false);
          } else {
            // Limited validity - redirect to dashboard
            setSuccessMessage(
              "Payment successful! Redirecting to dashboard..."
            );
            setPurchasing(false);

            // Show success message briefly before redirecting
            setTimeout(() => {
              navigate("/dashboard");
            }, 2000);
          }
        } catch (error) {
          console.error("Payment completion error:", error);
          setError("Payment verification failed. Please contact support.");
          setPurchasing(false);
        }
      },
      prefill: {
        name: currentUser?.displayName || "",
        email: currentUser?.email || "",
      },
      theme: {
        color: "#3B82F6",
      },
      modal: {
        ondismiss: () => {
          setPurchasing(false);
          setError("Payment was cancelled");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const goToStep = (stepNumber) => {
    if (stepNumber === 1) {
      setCurrentStep(1);
    } else if (stepNumber === 2 && selectedCarType) {
      setCurrentStep(2);
    } else if (stepNumber === 3 && selectedCarType && selectedPlan) {
      setCurrentStep(3);
    }
  };

  const goToNextStep = () => {
    if (currentStep === 1 && selectedCarType) {
      setCurrentStep(2);
    } else if (currentStep === 2 && selectedPlan) {
      handlePurchase();
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const getPlanPrice = (plan) => {
    if (!selectedCarType) return "N/A";

    const pricingInfo = plan.pricingPlanCarTypes?.find(
      (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
    );

    if (!pricingInfo) return "N/A";

    const originalPrice = parseFloat(pricingInfo.price);

    if (appliedCoupon) {
      const discountAmount = parseFloat(appliedCoupon.discountAmount);
      const finalPrice = Math.max(0, originalPrice - discountAmount);
      return `₹${finalPrice.toFixed(2)}`;
    }

    return `₹${originalPrice.toFixed(2)}`;
  };

  const getOriginalPrice = (plan) => {
    if (!selectedCarType) return 0;

    const pricingInfo = plan.pricingPlanCarTypes?.find(
      (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
    );

    return pricingInfo ? parseFloat(pricingInfo.price) : 0;
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {steps.map((step, index) => (
        <div key={step.id} className="step-item">
          <div
            className={`step-circle ${
              currentStep === step.id ? "active" : ""
            } ${completedSteps.has(step.id) ? "completed" : ""}`}
            onClick={() => goToStep(step.id)}
          >
            {completedSteps.has(step.id) ? "✓" : step.id}
          </div>
          <div className="step-info">
            <div className="step-title">{step.title}</div>
            <div className="step-description">{step.description}</div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`step-connector ${
                completedSteps.has(step.id) ? "completed" : ""
              }`}
            ></div>
          )}
        </div>
      ))}
    </div>
  );

  const renderCarTypeSelection = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>Select Your Car Type</h2>
        <p>Choose your vehicle type to see available pricing plans</p>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading car types...</p>
        </div>
      ) : (
        <div className="car-types-grid">
          {carTypes.map((carType) => (
            <div
              key={carType.id}
              className={`car-type-card ${
                selectedCarType?.id === carType.id ? "selected" : ""
              }`}
              onClick={() => handleCarTypeSelect(carType)}
            >
              <div className="car-type-icon">🚗</div>
              <h3>{carType.name}</h3>
              <div className="car-type-description">
                Perfect for {carType.name.toLowerCase()} vehicles
              </div>
            </div>
          ))}
        </div>
      )}

      {carTypes.length === 0 && !loading && (
        <div className="no-data">
          <p>No car types available at the moment.</p>
          <p>Please contact support for assistance.</p>
        </div>
      )}
    </div>
  );
  const renderPricingPlanSelection = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>
          {payPerUseOnly ? "Choose Your Pay-per-use Plan" : "Choose Your Plan"}
        </h2>
        <p>
          Selected vehicle: <strong>{selectedCarType?.name}</strong>
        </p>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading pricing plans...</p>
        </div>
      ) : (
        <div className="pricing-plans-grid">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-plan-card ${
                selectedPlan?.id === plan.id ? "selected" : ""
              }`}
              onClick={() => handlePlanSelect(plan)}
            >
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">{getPlanPrice(plan)}</div>
              </div>

              <div className="plan-features">
                <div className="feature">
                  <span className="feature-label">Credits:</span>
                  <span className="feature-value">{plan.creditsGiven}</span>
                </div>

                {plan.oneTimeCreditsGiven > 0 && (
                  <div className="feature">
                    <span className="feature-label">Bonus Credits:</span>
                    <span className="feature-value">
                      {plan.oneTimeCreditsGiven}
                    </span>
                  </div>
                )}

                <div className="feature">
                  <span className="feature-label">Validity:</span>
                  <span className="feature-value">
                    {plan.validity === -1
                      ? "Unlimited"
                      : `${plan.validity} days`}
                  </span>
                </div>
              </div>

              {plan.description && (
                <div className="plan-description">
                  <p>{plan.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pricingPlans.length === 0 && !loading && (
        <div className="no-data">
          <p>No pricing plans available for the selected car type.</p>
          <p>Please try selecting a different car type or contact support.</p>
        </div>
      )}
    </div>
  );

  const renderBookingCalendar = () => (
    <div className="step-content">
      <div className="step-header">
        <h2>Book Your Service</h2>
        <p>Schedule your car wash service</p>
        <div className="purchase-summary">
          <div className="summary-item">
            <span>Car Type:</span>
            <span>{selectedCarType?.name}</span>
          </div>
          <div className="summary-item">
            <span>Plan:</span>
            <span>{selectedPlan?.name}</span>
          </div>
          <div className="summary-item">
            <span>Credits:</span>
            <span>{selectedPlan?.creditsGiven}</span>
          </div>
        </div>
      </div>

      <BookingCalendar />
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCarTypeSelection();
      case 2:
        return renderPricingPlanSelection();
      case 3:
        return renderBookingCalendar();
      default:
        return null;
    }
  };

  const canProceedToNext = () => {
    if (currentStep === 1) return selectedCarType;
    if (currentStep === 2) return selectedPlan;
    return false;
  };

  return (
    <div className="integrated-purchase-flow">
      <header className="dashboard-header">
        <div className="header-content">
          <img src={logo} alt="Steam & Drive" className="dashboard-logo" />{" "}
          <div className="user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button
              onClick={() => navigate("/booking-history")}
              className="history-btn"
            >
              History
            </button>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="purchase-flow-container">
        {renderStepIndicator()}{" "}
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="success-message">
            <p>{successMessage}</p>
          </div>
        )}
        {renderStepContent()}
        {currentStep < 3 && (
          <div className="step-navigation">
            <button
              onClick={goToPreviousStep}
              disabled={currentStep === 1}
              className="nav-button prev-button"
            >
              ← Previous
            </button>

            <button
              onClick={goToNextStep}
              disabled={!canProceedToNext() || purchasing}
              className="nav-button next-button"
            >
              {currentStep === 2
                ? purchasing
                  ? "Processing..."
                  : "Purchase & Continue"
                : "Next →"}
            </button>
          </div>
        )}
        {selectedPlan && currentStep === 2 && (
          <div className="purchase-summary-bottom">
            <div className="summary-content">
              <h4>Purchase Summary</h4>
              <div className="summary-details">
                <div className="summary-row">
                  <span>Plan:</span>
                  <span>{selectedPlan.name}</span>
                </div>
                <div className="summary-row">
                  <span>Car Type:</span>
                  <span>{selectedCarType?.name}</span>
                </div>
                <div className="summary-row">
                  <span>Credits:</span>
                  <span>{selectedPlan.creditsGiven}</span>
                </div>
                {selectedPlan.oneTimeCreditsGiven > 0 && (
                  <div className="summary-row">
                    <span>Bonus Credits:</span>
                    <span>{selectedPlan.oneTimeCreditsGiven}</span>
                  </div>
                )}{" "}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>{getPlanPrice(selectedPlan)}</span>
                </div>
              </div>

              {/* Coupon Input */}
              <CouponInput
                onCouponApply={handleCouponApply}
                onCouponRemove={handleCouponRemove}
                appliedCoupon={appliedCoupon}
                originalAmount={getOriginalPrice(selectedPlan)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegratedPurchaseFlow;
